import { handleException } from 'clients/exception-handling'

export interface LocalStorageClient {
  getItem: (key: string) => string | undefined
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
  /** Clear all storage entries except those whose keys are in `preserveKeys`. */
  clearAllExcept: (preserveKeys: string[]) => void
  getJson: <T>(key: string, fallback: T) => T
  getOptionalJson: <T>(key: string) => T | undefined
  setJson: <T>(key: string, value: T) => void
}

type StorageProvider = () => Storage | undefined

const browserStorageProvider: StorageProvider = () => {
  if (typeof window === 'undefined') return undefined
  return window.localStorage
}

const unavailableStorageNames = new Set([
  'SecurityError',
  'QuotaExceededError',
  'NS_ERROR_DOM_QUOTA_REACHED',
])
const isStorageUnavailable = (cause: unknown): boolean =>
  cause instanceof Error && unavailableStorageNames.has(cause.name)

export class BrowserLocalStorageClient implements LocalStorageClient {
  constructor(private readonly storageProvider = browserStorageProvider) {}

  getItem(key: string): string | undefined {
    try {
      return this.storageProvider()?.getItem(key) ?? undefined
    } catch (error) {
      handleException(error, {
        operation: 'browser-storage.read',
        disposition: 'expected',
        isExpectedError: isStorageUnavailable,
        reason:
          'Browser storage is optional when browser policy or quota prevents access.',
      })
      return undefined
    }
  }

  setItem(key: string, value: string): void {
    try {
      this.storageProvider()?.setItem(key, value)
    } catch (error) {
      handleException(error, {
        operation: 'browser-storage.write',
        disposition: 'expected',
        isExpectedError: isStorageUnavailable,
        reason:
          'Browser storage is optional when browser policy or quota prevents access.',
      })
      // Browser storage can be unavailable in private mode or blocked contexts.
    }
  }

  removeItem(key: string): void {
    try {
      this.storageProvider()?.removeItem(key)
    } catch (error) {
      handleException(error, {
        operation: 'browser-storage.remove',
        disposition: 'expected',
        isExpectedError: isStorageUnavailable,
        reason:
          'Browser storage is optional when browser policy or quota prevents access.',
      })
      // Browser storage can be unavailable in private mode or blocked contexts.
    }
  }

  clearAllExcept(preserveKeys: string[]): void {
    try {
      const storage = this.storageProvider()
      if (!storage) return
      const preserved = preserveKeys.map((k): [string, string | null] => [
        k,
        storage.getItem(k),
      ])
      storage.clear()
      for (const [k, v] of preserved) if (v !== null) storage.setItem(k, v)
    } catch (error) {
      handleException(error, {
        operation: 'browser-storage.clear',
        disposition: 'expected',
        isExpectedError: isStorageUnavailable,
        reason:
          'Browser storage is optional when browser policy or quota prevents access.',
      })
      // Browser storage can be unavailable in private mode or blocked contexts.
    }
  }

  getJson<T>(key: string, fallback: T): T {
    return this.getOptionalJson<T>(key) ?? fallback
  }

  getOptionalJson<T>(key: string): T | undefined {
    const raw = this.getItem(key)
    if (!raw) return undefined
    try {
      return JSON.parse(raw) as T
    } catch (error) {
      handleException(error, {
        operation: 'browser-storage.parse',
        disposition: 'expected',
        isExpectedError: (cause) => cause instanceof SyntaxError,
        reason: 'Invalid cached JSON is treated as a missing optional value.',
      })
      return undefined
    }
  }

  setJson<T>(key: string, value: T): void {
    this.setItem(key, JSON.stringify(value))
  }
}

export const localStorageClient = new BrowserLocalStorageClient()
